import Box from '@mui/material/Box'
import Snackbar, { type SnackbarProps } from '@mui/material/Snackbar'
import Typography from '@mui/material/Typography'
import ButtonBase from '@mui/material/ButtonBase'
import { Icon } from '@tm/ui/atomic/Icon/Icon'
import { faClose } from '@tm/ui/icons/solid/faClose'
import { sand, amber } from '@tm/ui/theme/tokens/semantic'
import { PersistentSnackbar } from '@tm/ui/atomic/Toast/PersistentSnackbar'

/**
 * The shared toast: one dark pill for outcomes and undo, replacing the
 * per-feature <Snackbar><Alert/></Snackbar> pairs (this is the fourth pattern —
 * new code uses this one). It inverts against the app like the batch bar: dark
 * in light mode, light in dark mode. The amber action ink only rides the dark
 * pill (light mode, ~10:1 contrast); on the inverted light pill (dark mode) the
 * action uses the same ink as the message text instead, since amber never sits
 * on a white surface.
 *
 * Copy rules for every message that lands here (from the modernization doc §4.5):
 *   1. What broke — name the thing ("Couldn't hide 2 reviews"), never "Something went wrong".
 *   2. What it means — "They stay public until it works."
 *   3. The way out — one concrete action (Retry / Undo). Never "please try again later", never "Oops".
 */
export type ToastProps = {
  open: boolean
  message: string
  onClose: () => void
  action?: { label: string; onClick: () => void }
  /**
   * `null` keeps the toast up until it is dismissed — pair it with `dismissLabel`. Defaults to 6 s,
   * or `null` for a persistent toast.
   */
  autoHideDuration?: number | null
  /**
   * Renders a close button with this accessible label. A toast that never
   * times out needs one, otherwise the only way out is the action itself.
   */
  dismissLabel?: string
  /**
   * `persistent` moves the toast out of the bottom centre, where it would hide the action toasts,
   * and keeps it up until dismissed. See PersistentSnackbar.
   */
  placement?: 'action' | 'persistent'
}

function ActionSnackbar(props: Omit<SnackbarProps, 'anchorOrigin'>) {
  return <Snackbar {...props} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
}

export function Toast({
  open,
  message,
  onClose,
  action,
  autoHideDuration,
  dismissLabel,
  placement = 'action',
}: ToastProps) {
  const defaultHideAfter = placement === 'persistent' ? null : 6000
  const hideAfter = autoHideDuration === undefined ? defaultHideAfter : autoHideDuration
  const Bar = placement === 'persistent' ? PersistentSnackbar : ActionSnackbar
  return (
    <Bar
      open={open}
      onClose={(_, reason) => {
        // Undo toasts must survive a stray click elsewhere; only time and the
        // explicit action dismiss them.
        if (reason !== 'clickaway') onClose()
      }}
      autoHideDuration={hideAfter}>
      {/* Box, not Stack: Snackbar's Grow transition clones its direct child with its own
          `direction` prop ('up'/'down', for positioning), which would silently clobber Stack's
          same-named `direction` prop ('row') and fail Stack's PropTypes. Box declares no
          `direction` prop, so it isn't affected. */}
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        gap={1.5}
        sx={theme => {
          const isLight = theme.palette.mode === 'light'
          return {
            backgroundColor: isLight ? sand[900] : sand[0],
            color: isLight ? sand[25] : sand[900],
            borderRadius: 1,
            px: 1.75,
            py: 1.25,
            boxShadow: theme.shadows[8],
          }
        }}>
        <Typography variant="body2" component="span">
          {message}
        </Typography>
        {action && (
          <ButtonBase
            onClick={action.onClick}
            sx={theme => ({
              fontFamily: theme.typography.fontFamily,
              fontSize: theme.typography.body2.fontSize,
              fontWeight: 600,
              color: theme.palette.mode === 'light' ? amber[400] : sand[900],
              borderRadius: 0.75,
              px: 0.5,
            })}>
            {action.label}
          </ButtonBase>
        )}
        {dismissLabel && (
          <ButtonBase
            onClick={onClose}
            aria-label={dismissLabel}
            sx={{
              borderRadius: 0.75,
              // Optical balance against the pill's px, which the text does not need
              mr: -0.5,
              p: 0.5,
            }}>
            <Icon icon={faClose} sx={{ fontSize: '0.875rem' }} />
          </ButtonBase>
        )}
      </Box>
    </Bar>
  )
}
